import { forwardRef, memo, useMemo } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cx } from '@/utils/cx';

import { useIsDesktop, useScrollContext } from '../HeaderContext';

import styles from './HeaderBottom.module.scss';

import type { VisibleOn } from '../types';

export type BottomBehavior = 'collapse' | 'fixed' | 'reveal';

/**
 * 디바이스별로 다른 behavior 값이 필요할 때 쓰는 형태.
 * 단일 string 도 그대로 받으며, 객체로 전달하면 mobile/desktop 모두 명시 필수 (TypeScript 검출).
 */
export type ResponsiveBottomBehavior = BottomBehavior | { mobile: BottomBehavior; desktop: BottomBehavior };

/**
 * Bottom 슬롯의 실측 높이. Header root 가 children 순회로 합산하여 CSS 변수
 * (`--core-ui-header-bottom-height-mobile|desktop`) 를 root inline style 로 inject.
 * SSR 첫 paint 시점부터 spacer 높이가 정확하게 보장된다.
 *
 * **동작 조건**: `Header.Bottom` 이 `<Header>` 의 직접 자식이거나 `<Header.BottomWrapper>` 안에
 * 있어야 한다. 호스트 자체 컴포넌트로 `<Header.Bottom>` 을 감싸면 Header 가 element type 매칭
 * 으로 찾지 못한다 (React compound pattern 의 일반적 컨벤션 — 합성하려면 children 만 분리).
 * 그 경우 호스트가 `--core-ui-header-bottom-height-*` CSS 변수를 직접 선언해야 한다.
 */
export interface HeaderBottomHeight {
  mobile: number;
  desktop: number;
}

/**
 * `Header.Bottom` 의 `border` prop value shape.
 *
 * - `true`: 노출 + collapsed 시 opacity transition 으로 fade
 * - `'always'`: 항상 노출 (collapsed 무관)
 * - `false` / 미지정: 노출 안 함 (조건부 prop 또는 객체 spread 덮어쓰기에 유용)
 */
export type HeaderBottomBorderState = boolean | 'always';

export interface HeaderBottomBorder {
  top?: HeaderBottomBorderState;
  bottom?: HeaderBottomBorderState;
}

export interface HeaderBottomProps extends ComponentPropsWithoutRef<'div'> {
  /** 특정 디바이스에서만 노출. 미지정 = 항상 노출 */
  visibleOn?: VisibleOn;
  /**
   * Bottom 슬롯의 실측 높이(px). Header root 가 자동 합산하여 spacer 높이에 반영.
   * 합성 케이스(호스트 컴포넌트로 감싸기) 에서는 동작하지 않으므로 그때는 CSS 변수 사용.
   */
  height?: HeaderBottomHeight;
  /**
   * Bottom 슬롯의 상/하단 viewport 풀폭 가로 구분선. `collapseRoot::before` / `::after` 에 그려져
   * `max-width` 와 무관하게 풀폭으로 표시. 색상은 `--core-ui-header-bottom-border-color`
   * CSS 변수로 customize (기본 $opacity-black-5).
   *
   * value 의미:
   * - `true`: 노출. collapsed 상태에서 콘텐츠와 동기되어 opacity transition 으로 fade-out.
   * - `'always'`: collapsed 상태에서도 유지. 페이지 상단에 영구적으로 보여야 하는 sticky divider.
   * - 미지정: 노출 안 함.
   *
   * 스크롤바 폭 보정이 필요하면 호스트가 `width: calc(100vw - var(--scrollbar-width, 0px))` 로 escape.
   *
   * 미래 확장 — 다른 동작 modifier 가 필요해지면 string union 에 추가 (`'on-scroll'` 등, minor BC).
   */
  border?: HeaderBottomBorder;
  /**
   * Bottom 동작 모드. 단일 string 또는 `{ mobile, desktop }` 객체로 디바이스별 분기 가능.
   *
   * - `'collapse'`: 스크롤 시 자동 접힘 (기본값)
   * - `'fixed'`: 항상 고정 노출
   * - `'reveal'`: 스크롤 ↑ 노출, 스크롤 ↓ 접힘
   * - `{ mobile: 'collapse', desktop: 'fixed' }`: 디바이스별 다른 동작
   *
   * **왜 `behavior` 만 prop 반응형(`{ mobile, desktop }`)을 지원하나** —
   * Header.Bottom 의 다른 prop(`className`, `contentClassName`, `style`)은 호스트 측 CSS
   * `@media` 로 직접 분기 가능하므로 prop 반응형이 불필요하다. 반면 `behavior`는 JS runtime 분기점이라 CSS 로 표현 불가능하다. 즉 prop 반응형은
   * "CSS 로 풀 수 없는 prop 에만" 선택적으로 적용한다 (CSS/JS 책임 분리).
   *
   * 객체 형태로 전달하면 내부적으로 `useIsDesktop()` 으로 해석된다. SSR 환경에서는
   * 첫 paint 에 mobile 가정으로 출력되고 hydration 직후 실제 값으로 전환되나, behavior 의
   * 시각 차이는 *스크롤* 시점에만 드러나므로 실측상 사용자 눈에 띄지 않는다.
   */
  behavior?: ResponsiveBottomBehavior;
  /**
   * collapsed 상태에서도 wrapper 가 유지할 두께(px).
   *
   * default `0` = 완전 접힘 (기존 동작, BC). 양수 명시 시 자식은 `grid-template-rows: 0fr` 로 숨고
   * wrapper 자체만 그 두께 만큼 남아 *순수 background layer* 역할. 자식 잘림 없음.
   *
   * 명시 시 inline style 로 `--core-ui-header-bottom-collapsed-height` CSS 변수 inject —
   * SCSS 의 `&.collapsed { min-height: var(--core-ui-header-bottom-collapsed-height, 0) }` 가 적용.
   * grid-template-rows transition (0.25s) 과 동기되어 부드럽게 줄어듦.
   *
   * 사용 케이스: collapsed 상태에서도 *4px 정도 흰 배경 layer* 를 남겨 시각적 분리감 유지.
   *
   * 호스트가 직접 `--core-ui-header-bottom-collapsed-height` CSS 변수를 className 등으로 선언하는
   * 방식도 그대로 동작 (prop 미명시 시 그 값 사용).
   */
  collapsedHeight?: number;
  /** `.bottom(사용자가 주입한 slot wrapper)`에 합쳐 적용할 className */
  contentClassName?: string;
}

const isResponsiveBehavior = (
  value: ResponsiveBottomBehavior,
): value is { mobile: BottomBehavior; desktop: BottomBehavior } =>
  typeof value === 'object' && 'mobile' in value && 'desktop' in value;

export const HeaderBottom = memo(
  forwardRef<HTMLDivElement, HeaderBottomProps>(
    (
      // height 는 Header root 가 children inspection 으로 읽어 합산 — 컴포넌트 자체는 시각 처리에 사용하지 않으므로 destructure 만 하고 DOM 으로 흘리지 않는다.
      {
        children,
        className,
        style,
        contentClassName,
        visibleOn,
        behavior = 'collapse',
        height: _height,
        collapsedHeight,
        border,
        ...rest
      },
      ref,
    ) => {
      const { isScrolled, scrollDirection } = useScrollContext();
      const isDesktop = useIsDesktop();

      const resolvedBehavior = useMemo<BottomBehavior>(
        // eslint-disable-next-line no-nested-ternary
        () => (isResponsiveBehavior(behavior) ? (isDesktop ? behavior.desktop : behavior.mobile) : behavior),
        [behavior, isDesktop],
      );

      // direction 토글 진동 차단은 useScroll 의 cooldown 단계에서 처리되므로,
      // 여기서는 derived 값 그대로 DOM 에 반영한다.
      const shouldCollapse =
        (resolvedBehavior === 'collapse' && isScrolled) ||
        (resolvedBehavior === 'reveal' && scrollDirection === 'down');

      // collapsedHeight prop → CSS 변수 inject. 호스트 style 와 병합 (호스트 명시가 prop 보다 우선되도록 spread 순서 유지).
      const mergedStyle =
        collapsedHeight !== undefined
          ? ({ '--core-ui-header-bottom-collapsed-height': `${collapsedHeight}px`, ...style } as React.CSSProperties)
          : style;

      return (
        <div
          className={cx(
            styles.collapseRoot,
            shouldCollapse && styles.collapsed,
            border?.top && styles.borderTop,
            border?.top === 'always' && styles.borderTopAlways,
            border?.bottom && styles.borderBottom,
            border?.bottom === 'always' && styles.borderBottomAlways,
            className,
          )}
          data-core-ui-visible-on={visibleOn}
          style={mergedStyle}
        >
          <div className={styles.collapseInner}>
            <div className={cx(styles.bottom, contentClassName)} data-core-ui-header-slot="bottom" ref={ref} {...rest}>
              {children}
            </div>
          </div>
        </div>
      );
    },
  ),
);
HeaderBottom.displayName = 'Header.Bottom';
